import { PDFExporter } from './pdfExport';
import type { ForecastScenario, ForecastLineItem, ForecastCalculation } from '../hooks/useForecast';

function formatAmountForPDF(amount: number, currency: string): string {
  const formatted = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formatted} ${currency}`;
}

function formatAmountPlain(amount: number): string {
  return Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function exportForecastToPDF(
  scenario: ForecastScenario,
  lineItems: ForecastLineItem[],
  calculation: ForecastCalculation
) {
  const pdf = new PDFExporter();

  const startDate = new Date(scenario.start_date);
  const fiscalYearLabel = `${startDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} - ${new Date(startDate.getFullYear() + scenario.years_count, startDate.getMonth() - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`;

  pdf['addHeader']({
    title: scenario.name,
    subtitle: 'Compte d\'exploitation previsionnel',
    associationName: scenario.description || undefined,
    schoolYear: fiscalYearLabel
  });

  pdf.addSection('Synthese par annee');

  const kpiData = calculation.totals_by_year.map(total => ({
    annee: `Annee ${total.year_index}`,
    produits: formatAmountForPDF(total.total_revenues, scenario.currency),
    charges: formatAmountForPDF(total.total_expenses, scenario.currency),
    resultat: formatAmountForPDF(total.net_result, scenario.currency)
  }));

  pdf.addTable(
    [
      { header: 'Annee', dataKey: 'annee' },
      { header: 'Produits', dataKey: 'produits' },
      { header: 'Charges', dataKey: 'charges' },
      { header: 'Resultat Net', dataKey: 'resultat' }
    ],
    kpiData,
    {
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right', fontStyle: 'bold' }
      }
    }
  );

  const revenuesBySection = new Map<string, typeof lineItems>();
  const expensesBySection = new Map<string, typeof lineItems>();

  lineItems.forEach(line => {
    const map = line.line_type === 'revenue' ? revenuesBySection : expensesBySection;
    if (!map.has(line.section)) {
      map.set(line.section, []);
    }
    map.get(line.section)!.push(line);
  });

  const getLineAmount = (lineId: string, yearIndex: number): number => {
    const yearAmount = calculation.yearly_amounts.find(
      ya => ya.line_item_id === lineId && ya.year_index === yearIndex
    );
    return yearAmount ? yearAmount.amount : 0;
  };

  pdf.addSection('PRODUITS');

  Array.from(revenuesBySection.entries()).forEach(([section, lines]) => {
    pdf.addText(section, 11, true);

    const sectionData: any[] = [];
    lines.forEach(line => {
      const rowData: any = { libelle: '  ' + line.label };
      for (let i = 1; i <= scenario.years_count; i++) {
        const amount = getLineAmount(line.id, i);
        rowData[`annee${i}`] = amount > 0 ? formatAmountPlain(amount) : '-';
      }
      sectionData.push(rowData);
    });

    const sectionTotalRow: any = { libelle: 'Total ' + section };
    for (let i = 1; i <= scenario.years_count; i++) {
      const sectionTotal = lines.reduce((sum, line) => sum + getLineAmount(line.id, i), 0);
      sectionTotalRow[`annee${i}`] = formatAmountPlain(sectionTotal);
    }
    sectionData.push(sectionTotalRow);

    const columns = [
      { header: 'Libelle', dataKey: 'libelle' },
      ...Array.from({ length: scenario.years_count }, (_, i) => ({
        header: `Annee ${i + 1}`,
        dataKey: `annee${i + 1}`
      }))
    ];

    pdf.addTable(columns, sectionData, {
      columnStyles: {
        0: { halign: 'left', cellWidth: 60 },
        ...Object.fromEntries(
          Array.from({ length: scenario.years_count }, (_, i) => [
            i + 1,
            { halign: 'right', cellWidth: (170 - 60) / scenario.years_count }
          ])
        )
      }
    });
  });

  const totalRevenues: any = { libelle: 'TOTAL PRODUITS' };
  for (let i = 1; i <= scenario.years_count; i++) {
    const total = calculation.totals_by_year.find(t => t.year_index === i);
    totalRevenues[`annee${i}`] = total ? formatAmountPlain(total.total_revenues) : '0';
  }

  const totalRevenuesColumns = [
    { header: 'Libelle', dataKey: 'libelle' },
    ...Array.from({ length: scenario.years_count }, (_, i) => ({
      header: `Annee ${i + 1}`,
      dataKey: `annee${i + 1}`
    }))
  ];

  pdf.addTable(totalRevenuesColumns, [totalRevenues], {
    headStyles: { fillColor: [34, 197, 94] },
    columnStyles: {
      0: { halign: 'left', cellWidth: 60, fontStyle: 'bold' },
      ...Object.fromEntries(
        Array.from({ length: scenario.years_count }, (_, i) => [
          i + 1,
          { halign: 'right', cellWidth: (170 - 60) / scenario.years_count, fontStyle: 'bold' }
        ])
      )
    }
  });

  pdf.addSection('CHARGES');

  Array.from(expensesBySection.entries()).forEach(([section, lines]) => {
    pdf.addText(section, 11, true);

    const sectionData: any[] = [];
    lines.forEach(line => {
      const rowData: any = { libelle: '  ' + line.label };
      for (let i = 1; i <= scenario.years_count; i++) {
        const amount = getLineAmount(line.id, i);
        rowData[`annee${i}`] = amount > 0 ? formatAmountPlain(amount) : '-';
      }
      sectionData.push(rowData);
    });

    const sectionTotalRow: any = { libelle: 'Total ' + section };
    for (let i = 1; i <= scenario.years_count; i++) {
      const sectionTotal = lines.reduce((sum, line) => sum + getLineAmount(line.id, i), 0);
      sectionTotalRow[`annee${i}`] = formatAmountPlain(sectionTotal);
    }
    sectionData.push(sectionTotalRow);

    const columns = [
      { header: 'Libelle', dataKey: 'libelle' },
      ...Array.from({ length: scenario.years_count }, (_, i) => ({
        header: `Annee ${i + 1}`,
        dataKey: `annee${i + 1}`
      }))
    ];

    pdf.addTable(columns, sectionData, {
      columnStyles: {
        0: { halign: 'left', cellWidth: 60 },
        ...Object.fromEntries(
          Array.from({ length: scenario.years_count }, (_, i) => [
            i + 1,
            { halign: 'right', cellWidth: (170 - 60) / scenario.years_count }
          ])
        )
      }
    });
  });

  const totalExpenses: any = { libelle: 'TOTAL CHARGES' };
  for (let i = 1; i <= scenario.years_count; i++) {
    const total = calculation.totals_by_year.find(t => t.year_index === i);
    totalExpenses[`annee${i}`] = total ? formatAmountPlain(total.total_expenses) : '0';
  }

  const totalExpensesColumns = [
    { header: 'Libelle', dataKey: 'libelle' },
    ...Array.from({ length: scenario.years_count }, (_, i) => ({
      header: `Annee ${i + 1}`,
      dataKey: `annee${i + 1}`
    }))
  ];

  pdf.addTable(totalExpensesColumns, [totalExpenses], {
    headStyles: { fillColor: [239, 68, 68] },
    columnStyles: {
      0: { halign: 'left', cellWidth: 60, fontStyle: 'bold' },
      ...Object.fromEntries(
        Array.from({ length: scenario.years_count }, (_, i) => [
          i + 1,
          { halign: 'right', cellWidth: (170 - 60) / scenario.years_count, fontStyle: 'bold' }
        ])
      )
    }
  });

  pdf.addSection('RESULTAT NET');

  const netResult: any = { libelle: 'RESULTAT NET' };
  for (let i = 1; i <= scenario.years_count; i++) {
    const total = calculation.totals_by_year.find(t => t.year_index === i);
    netResult[`annee${i}`] = total ? formatAmountPlain(total.net_result) : '0';
  }

  const netResultColumns = [
    { header: 'Libelle', dataKey: 'libelle' },
    ...Array.from({ length: scenario.years_count }, (_, i) => ({
      header: `Annee ${i + 1}`,
      dataKey: `annee${i + 1}`
    }))
  ];

  pdf.addTable(netResultColumns, [netResult], {
    headStyles: { fillColor: [59, 130, 246] },
    columnStyles: {
      0: { halign: 'left', cellWidth: 60, fontStyle: 'bold' },
      ...Object.fromEntries(
        Array.from({ length: scenario.years_count }, (_, i) => [
          i + 1,
          { halign: 'right', cellWidth: (170 - 60) / scenario.years_count, fontStyle: 'bold' }
        ])
      )
    }
  });

  pdf.generate({
    title: scenario.name,
    subtitle: 'Compte d\'exploitation previsionnel'
  });
}

export function exportForecastToExcel(
  scenario: ForecastScenario,
  lineItems: ForecastLineItem[],
  calculation: ForecastCalculation
) {
  const getLineAmount = (lineId: string, yearIndex: number): number => {
    const yearAmount = calculation.yearly_amounts.find(
      ya => ya.line_item_id === lineId && ya.year_index === yearIndex
    );
    return yearAmount ? yearAmount.amount : 0;
  };

  const revenuesBySection = new Map<string, typeof lineItems>();
  const expensesBySection = new Map<string, typeof lineItems>();

  lineItems.forEach(line => {
    const map = line.line_type === 'revenue' ? revenuesBySection : expensesBySection;
    if (!map.has(line.section)) {
      map.set(line.section, []);
    }
    map.get(line.section)!.push(line);
  });

  let csv = 'Libelle';
  for (let i = 1; i <= scenario.years_count; i++) {
    csv += `;Annee ${i}`;
  }
  csv += '\n';

  csv += '\nPRODUITS\n';

  Array.from(revenuesBySection.entries()).forEach(([section, lines]) => {
    csv += `\n${section}\n`;

    lines.forEach(line => {
      csv += `  ${line.label}`;
      for (let i = 1; i <= scenario.years_count; i++) {
        const amount = getLineAmount(line.id, i);
        csv += `;${amount > 0 ? amount.toFixed(2) : '0'}`;
      }
      csv += '\n';
    });

    csv += `Total ${section}`;
    for (let i = 1; i <= scenario.years_count; i++) {
      const sectionTotal = lines.reduce((sum, line) => sum + getLineAmount(line.id, i), 0);
      csv += `;${sectionTotal.toFixed(2)}`;
    }
    csv += '\n';
  });

  csv += '\nTOTAL PRODUITS';
  for (let i = 1; i <= scenario.years_count; i++) {
    const total = calculation.totals_by_year.find(t => t.year_index === i);
    csv += `;${total ? total.total_revenues.toFixed(2) : '0'}`;
  }
  csv += '\n';

  csv += '\nCHARGES\n';

  Array.from(expensesBySection.entries()).forEach(([section, lines]) => {
    csv += `\n${section}\n`;

    lines.forEach(line => {
      csv += `  ${line.label}`;
      for (let i = 1; i <= scenario.years_count; i++) {
        const amount = getLineAmount(line.id, i);
        csv += `;${amount > 0 ? amount.toFixed(2) : '0'}`;
      }
      csv += '\n';
    });

    csv += `Total ${section}`;
    for (let i = 1; i <= scenario.years_count; i++) {
      const sectionTotal = lines.reduce((sum, line) => sum + getLineAmount(line.id, i), 0);
      csv += `;${sectionTotal.toFixed(2)}`;
    }
    csv += '\n';
  });

  csv += '\nTOTAL CHARGES';
  for (let i = 1; i <= scenario.years_count; i++) {
    const total = calculation.totals_by_year.find(t => t.year_index === i);
    csv += `;${total ? total.total_expenses.toFixed(2) : '0'}`;
  }
  csv += '\n';

  csv += '\nRESULTAT NET';
  for (let i = 1; i <= scenario.years_count; i++) {
    const total = calculation.totals_by_year.find(t => t.year_index === i);
    csv += `;${total ? total.net_result.toFixed(2) : '0'}`;
  }
  csv += '\n';

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${scenario.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
